import React, { useRef, useEffect } from "react";
import kiraLogo from "../../../assets/Kira.png";
import type { TranscriptItem } from "../hooks/useTranslator";
import { useUiLang } from "../../../context/UiLangContext";

interface SubtitleViewProps {
  meetingTranscript: TranscriptItem[];
  subtitleOrig: string;
  subtitleTran: string;
  srcLang: string;
  tgtLang: string;
  recording: boolean;
  isConnecting?: boolean;
  onToggleRecording: () => void;
}

export const SubtitleView: React.FC<SubtitleViewProps> = ({
  meetingTranscript,
  subtitleOrig,
  subtitleTran,
  srcLang,
  tgtLang,
  recording,
  isConnecting,
  onToggleRecording,
}) => {
  const { uiLang } = useUiLang();
  const isEn = uiLang === "en";
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll history when new items arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [meetingTranscript.length]);

  const steps = isEn
    ? [
        "Choose the source and target languages in the top bar.",
        <><strong className="text-[var(--text)] font-semibold">Click Start</strong> and select the system tab or window with audio.</>,
        "Done! Kira will capture the audio and show the translation instantly.",
      ]
    : [
        "Elige los idiomas de origen y destino en la barra superior.",
        <><strong className="text-[var(--text)] font-semibold">Haz clic en Iniciar</strong> y selecciona la pestaña o ventana del sistema con audio.</>,
        "¡Listo! Kira capturará el audio y mostrará la traducción al instante.",
      ];

  const hasActiveSession = recording || meetingTranscript.length > 0 || subtitleOrig || subtitleTran || isConnecting;

  return (
    <div className="flex-1 flex flex-col min-h-0 relative h-full overflow-hidden bg-[var(--bg2)]">
      {/* 1. Idle State / Before Recording */}
      {!hasActiveSession && (
        <div className="flex-1 flex flex-col items-center justify-center p-6 md:p-12 overflow-y-auto">
          <div className="max-w-[480px] w-full text-center flex flex-col items-center animate-fade-in my-auto">
            <div className="w-[220px] h-[220px] mb-[20px] flex items-center justify-center relative">
              <img
                src={kiraLogo}
                className="max-w-full max-h-full object-contain filter drop-shadow-[0_8px_16px_rgba(0,0,0,0.35)] animate-[gentle-float_6s_ease-in-out_infinite]"
                alt="Kira Logo"
              />
            </div>
            <p className="text-[12.5px] text-[var(--accent)] font-medium mb-[24px]">
              {isEn ? "Real-time translator" : "Traductor en tiempo real"}
            </p>

            <div className="w-full bg-[var(--bg3)] border border-[var(--b1)] py-[16px] px-[20px] rounded-[12px] text-left space-y-[11px] mb-[18px]">
              {steps.map((step, i) => (
                <div key={i} className="flex gap-[11px] items-start">
                  <span className="w-[20px] h-[20px] rounded-full bg-[var(--bg4)] border border-[var(--b2)] flex items-center justify-center text-[10.5px] font-bold text-[var(--text-muted)] flex-shrink-0 mt-[1px]">
                    {i + 1}
                  </span>
                  <p className="text-[13px] text-[var(--text-muted)] leading-[1.55]">{step}</p>
                </div>
              ))}
            </div>

            <div className="inline-flex items-center gap-[7px] border border-[var(--b1)] bg-[var(--bg3)] rounded-full px-[15px] py-[6px] text-[12px] text-[var(--text-dim)]">
              <span className="w-[6px] h-[6px] bg-[var(--text-dim)] rounded-full inline-block animate-pulse" />
              {isEn ? "Waiting for system audio..." : "Esperando audio del sistema..."}
            </div>
          </div>
        </div>
      )}

      {/* 2. Active Session View (Matching INTERFAZ_DE_KIRA.html) */}
      {hasActiveSession && (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden relative">
          {/* Top Session Header */}
          <div className="px-6 md:px-[40px] pt-[14px] pb-[10px] flex items-center gap-[8px] flex-shrink-0 border-b border-[var(--b1)] bg-[var(--bg2)]">
            <span className="text-[11px] font-[700] tracking-[0.08em] uppercase text-[var(--text-dim)]">
              {isEn ? "CURRENT SESSION" : "SESIÓN EN CURSO"}
            </span>
            <span className="text-[9.5px] font-[700] tracking-[0.04em] px-[8px] py-[2px] rounded-full bg-blue-500/12 text-[var(--accent)]">
              {isEn ? "Translator + Minutes" : "Traductor + Minuta"}
            </span>
          </div>

          {/* Scrollable History List */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-6 md:px-[40px] py-[20px] flex flex-col gap-[20px] scroll-bg scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent"
          >
            {meetingTranscript.map((item, index) => (
              <div key={index} className="flex flex-col gap-[4px] animate-fade-in">
                <div className="text-[10px] text-[var(--text-dim)] font-mono">
                  {item.timestamp} · {srcLang === "auto" ? "EN" : srcLang.toUpperCase()} → {tgtLang.toUpperCase()}
                </div>
                {item.orig && (
                  <div className="text-[13px] text-[var(--text-muted)] italic leading-[1.5] select-text">
                    {item.orig}
                  </div>
                )}
                <div className="text-[18px] font-[700] text-[var(--text)] leading-[1.4] tracking-[-0.02em] select-text">
                  {item.tran || item.orig}
                </div>
              </div>
            ))}
          </div>

          {/* Fixed Bottom Live Segment */}
          <div className="px-6 md:px-[40px] pt-[24px] pb-[28px] border-t border-[var(--b1)] bg-[var(--bg2)] flex flex-col gap-[6px] flex-shrink-0 relative">
            <div className="inline-flex items-center gap-[5px] text-[9.5px] font-[700] tracking-[0.06em] uppercase bg-red-500/12 text-red-400 px-[9px] py-[2px] rounded-full w-fit mb-[4px]">
              <div className="w-[5px] h-[5px] rounded-full bg-red-400 animate-pulse" />
              <span>{isEn ? "LIVE" : "EN VIVO"}</span>
            </div>

            {subtitleOrig && (
              <div className="text-[13px] text-[var(--text-muted)] italic leading-[1.5] select-text animate-fade-in">
                {subtitleOrig}
              </div>
            )}

            <div
              className={`text-[22px] sm:text-[26px] font-[800] leading-[1.35] tracking-[-0.03em] select-text animate-fade-in ${
                subtitleTran ? "text-[var(--text)]" : "text-[var(--text-dim)] font-normal text-base"
              }`}
            >
              {subtitleTran || (isConnecting ? (isEn ? "Connecting to audio..." : "Conectando al audio...") : (isEn ? "Listening..." : "Escuchando..."))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
